/*
 * Teng Tools USA — inline unit converters for the conversion-chart articles.
 *
 * PROGRESSIVE ENHANCEMENT, STRICTLY. The <table> in the article body is the
 * asset: it is server-rendered, it is what Google indexes, and it is the ONLY
 * thing AI answer engines see, because OAI-SearchBot, PerplexityBot and the
 * rest fetch raw HTML and do not execute JavaScript. This file adds a
 * convenience for humans on top of that. If it never loads, the page is still
 * complete and still citable.
 *
 * THIS SCRIPT BUILDS NO MARKUP. The label, input and output are rendered
 * server-side by scripts/teng/store-sync/blog-tables.mjs; all this does is drop
 * the `disabled` attribute and attach a listener. An earlier version built the
 * DOM here, which hydrated a reserved 152px box into 287px and shoved the table
 * down 135px on every page — the damaging direction of layout shift, and
 * unfixable by reserving a height because the rendered height differs per
 * article. Markup in the HTML means the box is final at first paint.
 *
 * NO DUPLICATED CONVERSION DATA. A calculator that disagrees with the table
 * printed beneath it is worse than no calculator, so nothing here defines a
 * conversion factor of its own:
 *   - linear converters read the exact factor from `data-factor`, which the
 *     generator writes from the same constant used to build the table;
 *   - lookup converters parse the rendered table out of the DOM, so they cannot
 *     drift from it at all.
 */
(function () {
  'use strict';

  function parseNumber(v) {
    var n = parseFloat(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : null;
  }

  function fmt(n, dp) {
    if (!Number.isFinite(n)) return '';
    var s = n.toFixed(dp);
    if (s.indexOf('.') !== -1) s = s.replace(/0+$/, '').replace(/\.$/, '');
    return s;
  }

  /** The article's own rendered table: headers plus rows of cell text. */
  function readTable(root) {
    var table = null;
    var el = root;
    while (el && !table) { el = el.nextElementSibling; if (el && el.tagName === 'TABLE') table = el; }
    if (!table && root.parentNode) table = root.parentNode.querySelector('table');
    if (!table) return null;

    var headers = Array.prototype.map.call(
      table.querySelectorAll('thead th'),
      function (th) { return (th.textContent || '').trim(); },
    );
    var rows = Array.prototype.map.call(table.querySelectorAll('tbody tr'), function (tr) {
      return Array.prototype.map.call(tr.children, function (td) {
        return (td.textContent || '').trim();
      });
    });
    return { headers: headers, rows: rows };
  }

  /**
   * Find the closest value across EVERY numeric column, not just the first.
   *
   * The first version scanned only the first numeric cell in each row. On the
   * drill chart the size column ("#7", "Z") does not parse as a number, so it
   * always fell through to inches — and a reader typing 6 meaning 6 mm was
   * silently matched against inches and handed drill Z. Scanning every column
   * and naming which one matched is what makes the answer trustworthy.
   */
  function lookup(data, value) {
    var best = null;
    var bestDiff = Infinity;
    var bestCol = -1;
    data.rows.forEach(function (cells) {
      cells.forEach(function (cell, col) {
        var n = parseNumber(cell);
        if (n === null) return;
        var d = Math.abs(n - value);
        if (d < bestDiff) { bestDiff = d; best = cells; bestCol = col; }
      });
    });
    if (!best) return '';
    var col = data.headers[bestCol] || 'value';
    return 'Closest by ' + col + ': ' + best.join('  ·  ');
  }

  function wire(root) {
    var mode = root.getAttribute('data-teng-converter');
    var input = root.querySelector('input');
    var out = root.querySelector('.teng-conv__out');
    if (!input || !out) return;

    var fromUnit = root.getAttribute('data-from') || '';
    var toUnit = root.getAttribute('data-to') || '';
    var factor = parseFloat(root.getAttribute('data-factor'));
    var dp = parseInt(root.getAttribute('data-dp'), 10);
    if (!Number.isFinite(dp)) dp = 4;

    var data = mode === 'lookup' ? readTable(root) : null;
    // A lookup with no table to read would be a box that never answers. Leave
    // it disabled rather than presenting an input that silently does nothing.
    if (mode === 'lookup' && (!data || !data.rows.length)) return;

    /**
     * Three-way switcher. The buttons choose which unit you are TYPING; both
     * other currencies render at once, so "what is this in euros and in
     * pounds" is one action rather than two.
     *
     * Every rate comes from `data-to-base` on the buttons, which the generator
     * writes from the same FX snapshot that built the table below — nothing
     * here defines a conversion of its own, so the switcher cannot disagree
     * with the printed figures.
     */
    if (mode === 'multi') {
      var unitEls = Array.prototype.slice.call(root.querySelectorAll('.teng-conv__unit'));
      // A switcher with nothing to switch between would be a control that
      // never answers. Leave it disabled rather than presenting dead buttons.
      if (unitEls.length < 2) return;

      var units = unitEls.map(function (el) {
        return {
          el: el,
          toBase: parseFloat(el.getAttribute('data-to-base')),
          short: el.getAttribute('data-short') || '',
        };
      });
      if (units.some(function (u) { return !Number.isFinite(u.toBase) || u.toBase <= 0; })) return;

      var active = units[0];

      var renderMulti = function () {
        var v = parseNumber(input.value);
        if (v === null) { out.textContent = ''; return; }
        var others = units.filter(function (u) { return u !== active; });
        out.textContent = fmt(v, 4) + ' ' + active.short + '  =  ' + others.map(function (u) {
          return fmt((v * active.toBase) / u.toBase, dp) + ' ' + u.short;
        }).join('   ·   ');
      };

      units.forEach(function (u) {
        u.el.addEventListener('click', function () {
          active = u;
          units.forEach(function (o) {
            o.el.classList.toggle('is-active', o === active);
            o.el.setAttribute('aria-pressed', o === active ? 'true' : 'false');
          });
          renderMulti();
          // Switching unit is a correction of what you already typed, so put
          // the caret back where it was rather than making the reader re-click.
          input.focus();
        });
        u.el.removeAttribute('disabled');
      });

      input.addEventListener('input', renderMulti);
      input.removeAttribute('disabled');
      return;
    }

    function render() {
      var v = parseNumber(input.value);
      if (v === null) { out.textContent = ''; return; }

      if (mode === 'linear') {
        if (!Number.isFinite(factor)) { out.textContent = ''; return; }
        out.textContent = fmt(v, 4) + ' ' + fromUnit + ' = ' + fmt(v * factor, dp) + ' ' + toUnit
          + '   ·   ' + fmt(v, 4) + ' ' + toUnit + ' = ' + fmt(v / factor, dp) + ' ' + fromUnit;
        return;
      }

      // Reports the closest LISTED row rather than interpolating: these are
      // discrete standard sizes, and a value between two drill gauges names a
      // drill that does not exist.
      out.textContent = lookup(data, v);
    }

    input.addEventListener('input', render);
    input.removeAttribute('disabled');
  }

  function init() {
    var nodes = document.querySelectorAll('[data-teng-converter]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-teng-ready') === '1') continue;
      try {
        wire(nodes[i]);
        nodes[i].setAttribute('data-teng-ready', '1');
      } catch (e) {
        // A converter that throws must never take the article down with it, and
        // must never leave an enabled-looking input that does nothing.
        if (window.console && console.warn) console.warn('teng-converter failed', e);
      }
    }
  }

  // Exposed deliberately. `init` is idempotent — it skips anything already
  // carrying data-teng-ready — so re-running it is safe, and exposing it lets
  // the test suite wire fresh markup by calling this entry point rather than by
  // dynamically evaluating this file's source. That is how the three-way
  // switcher came to have the first real test any of this script has had. It
  // also lets markup injected after page load be wired, which nothing does yet.
  window.TengConverter = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
